/**
 * Drag-resize Curriculum Builder Learning Path column (left edge).
 * Width clamped between MIN_WIDTH and 50% of the viewport.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export const LEARNING_PATH_DEFAULT_WIDTH = 360;
const MIN_WIDTH = 280;
const MAX_RATIO = 0.5;

export function clampLearningPathWidth(px: number): number {
  if (typeof window === 'undefined') return px;
  const max = Math.floor(window.innerWidth * MAX_RATIO);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(px)));
}

export function useLearningPathResize(
  defaultWidth: number = LEARNING_PATH_DEFAULT_WIDTH,
) {
  const [width, setWidth] = useState(() =>
    clampLearningPathWidth(
      typeof window !== 'undefined'
        ? Math.min(defaultWidth, Math.floor(window.innerWidth * 0.33))
        : defaultWidth,
    ),
  );
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Left edge: drag left = wider, drag right = narrower
      const next = drag.startWidth + (drag.startX - e.clientX);
      setWidth(clampLearningPathWidth(next));
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setWidth((w) => clampLearningPathWidth(w));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = { startX: e.clientX, startWidth: width };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  return { width, onResizePointerDown };
}
